import { Text } from '@fluentui/react-components';
import { LinkRegular } from '@fluentui/react-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetsHeadline } from '../WidgetsHeadline/WidgetsHeadline';
import { TileGrid } from '../../TileGrid/TileGrid';
import { LinkTile } from './LinkTile';
import { SchoolLink } from './SchoolLinks.interface';
import { SchoolLinksWidgetSkeleton } from './SchoolLinksWidgetSkeleton';
import cn from 'classnames';
import { SchoolLink as SchoolLinkTeamsServiceType } from '@mnspro-cloud/teams-services';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../Communication/CommunicationMessage';
import './SchoolLinksWidget.scss';

export interface SchoolLinksWidgetProperties {
  isLoading: boolean;
  favorites: (SchoolLink | SchoolLinkTeamsServiceType)[];
  error?: Error | null;
  onRetry?: () => void;
  classNames?: string;
  useDarkIllustration?: boolean;
  /**
   * Forwarded to each {@link LinkTile}. When omitted, tiles fall back to
   * `window.open`. Teams consumers pass an `app.openLink`-based handler.
   */
  onOpenLink?: (url: string) => void;
}

export const SchoolLinksWidget: React.FC<SchoolLinksWidgetProperties> = ({
  favorites,
  isLoading,
  error,
  onRetry,
  classNames,
  useDarkIllustration = false,
  onOpenLink,
}) => {
  const { t } = useTranslation();

  return (
    <div
      data-cy="schoolLinksWidget"
      className={cn('schoolLinksWidget flex flex-col', classNames)}
    >
      <WidgetsHeadline
        content={t('dashboard:schoolLinks:schoolLinks', { defaultValue: 'Schullinks' })}
        icon={<LinkRegular fontSize={24} />}
      />
      {!isLoading && !error && (
        <>
          {favorites.length > 0 ? (
            <TileGrid>
              {favorites.map((link, index) => (
                <LinkTile
                  key={index}
                  link={link}
                  showFavoriteStar={false}
                  onOpenLink={onOpenLink}
                />
              ))}
            </TileGrid>
          ) : (
            <CommunicationMessage
              illustration={Illustrations.Empty}
              headline=""
              text={t('dashboard:schoolLinks:emptyMessageText', { defaultValue: 'Keine Links vorhanden' })}
              illustrationSize={IllustrationSize.Small}
              useDarkIllustration={useDarkIllustration}
            />
          )}
        </>
      )}
      {isLoading && <SchoolLinksWidgetSkeleton />}
      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3">
          <Text className="text-error">
            {t('dashboard:schoolLinks:errorMessageText', { defaultValue: 'Fehler beim Laden der Schullinks' })}
          </Text>
          {onRetry && (
            <button onClick={onRetry} className="btn-retry">
              {t('dashboard:schoolLinks:retry', { defaultValue: 'Neu laden' })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
